import {
  encodeUploadFileName,
  getDocumentDisplayName,
  IResponse,
  Media,
  ProfilePicSizes,
  UPLOAD_FILE_PREFIXES,
} from "@eventr/common";
import { StateCreator } from "zustand";
import { EventrGatewayAPI } from "../../data/gateway.api";
import { BoundStore } from "../../state/bound.store";

type MediaType = "HeaderImage" | "Document" | "ProfilePic" | "Document-Uploaded" | "Note";
interface MediaState {}

interface MediaActions {
  uploadMedia: (file: File, type: MediaType, org?: string) => Promise<IResponse<Media>>;
}
export interface IMediaSlice extends MediaState, MediaActions {}
export const createMediaSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], IMediaSlice> = (
  set,
  get,
) => ({
  uploadMedia: async (file: File, type: MediaType, org?: string) => {
    const formData = new FormData();
    const fileName = getFilePrefix(type) + encodeUploadFileName(file.name);
    formData.append("file", file, fileName);
    const orgName = org || get().selectedOrg?.name;

    return EventrGatewayAPI.media.upload(formData, orgName);
  },
});

const getFilePrefix = (type: MediaType) => {
  const filePrefixes: Record<MediaType, string> = {
    HeaderImage: UPLOAD_FILE_PREFIXES.header,
    ProfilePic: UPLOAD_FILE_PREFIXES.profilePic,
    Document: UPLOAD_FILE_PREFIXES.legacyDocument,
    "Document-Uploaded": UPLOAD_FILE_PREFIXES.document,
    Note: UPLOAD_FILE_PREFIXES.note,
  };

  return filePrefixes[type] || UPLOAD_FILE_PREFIXES.untyped;
};

/**
 * The name to show for a stored media URL: last path segment, percent-decoded, upload prefix
 * removed and any characters escaped past WordPress' `sanitize_file_name()` restored.
 *
 * Pass the whole URL - it does the splitting itself.
 */
export const getFileName = getDocumentDisplayName;

export const getMediaSizes = (image: Media): ProfilePicSizes | null => {
  if (!image) return null;
  const sizes = image.media_details?.sizes;
  if (!sizes) return null;
  const resp: ProfilePicSizes = {};
  const medium = sizes.medium?.source_url;
  const tiny = sizes.thumbnail?.source_url;

  if (medium) {
    resp.medium = { url: medium };
  }
  if (tiny) {
    resp.tiny = { url: tiny };
  } else {
    resp.tiny = { url: image.source_url };
  }
  return resp;
};
